import { useState, useEffect, useRef } from 'react'
import { Layout as AntLayout, Menu, Avatar, Dropdown, Badge, Button, Tooltip, Popover, notification } from 'antd'
import {
    LogoutOutlined,
    SunOutlined,
    MoonOutlined,
    BellOutlined,
    BellFilled,
    MenuFoldOutlined,
    MenuUnfoldOutlined,
} from '@ant-design/icons'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '../../store/authStore'
import { useNotificationStore } from '../../store/notificationStore'
import { NotificationList } from '../Notifications/NotificationList'
import { ChatNotificationList } from '../Notifications/ChatNotificationList'
import { useTheme } from '../../contexts/ThemeContext'
import { internalChatsApi } from '../../services/api'
import {
    DashboardIcon,
    ChatsIcon,
    ContactsIcon,
    OperatorsIcon,
    TelegramAccountIcon,
    ProxyIcon,
    SettingsIcon,
    SendIcon,
    CalendarIcon,
    TasksIcon,
} from '../Icons'

const { Sider, Header, Content } = AntLayout

interface LayoutProps {
    children: React.ReactNode
}

export default function Layout({ children }: LayoutProps) {
    const [collapsed, setCollapsed] = useState(false)
    const [notificationOpen, setNotificationOpen] = useState(false)
    const [chatPopoverOpen, setChatPopoverOpen] = useState(false)
    const [unreadChatCount, setUnreadChatCount] = useState(0)
    const [notificationsEnabled, setNotificationsEnabled] = useState(() =>
        localStorage.getItem('tg_notifications_enabled') !== 'false'
    )
    const notificationsEnabledRef = useRef(notificationsEnabled)
    const navigate = useNavigate()
    const location = useLocation()
    const { user, logout } = useAuthStore()
    const { unreadCount, fetchUnreadCount, addNotification, addTgMessage, tgUnreadCount, clearTgUnread } = useNotificationStore()
    const { theme, toggleTheme } = useTheme()
    const [api, contextHolder] = notification.useNotification()

    useEffect(() => {
        notificationsEnabledRef.current = notificationsEnabled
    }, [notificationsEnabled])

    useEffect(() => {
        fetchUnreadCount()
        fetchUnreadChats()

        // Backup polling (every 5 mins)
        const interval = setInterval(() => {
            fetchUnreadCount()
            fetchUnreadChats()
        }, 60000) // Increased to 1 min for chats

        // WebSocket с автореконнектом
        const token = localStorage.getItem('token')
        let ws: WebSocket | null = null
        let reconnectTimeout: ReturnType<typeof setTimeout> | null = null
        let reconnectDelay = 1000
        let wsIsClosed = false

        const connectWs = () => {
            if (!token || wsIsClosed) return
            const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
            const wsUrl = `${wsProtocol}//${window.location.host}/ws?token=${token}`

            ws = new WebSocket(wsUrl)
            ws.onopen = () => { reconnectDelay = 1000 }

            ws.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data)
                    if (data.type === 'notification') {
                        addNotification(data.data)
                        if (notificationsEnabledRef.current) {
                            api.info({
                                message: data.data.title || 'Новое уведомление',
                                description: data.data.message,
                                placement: 'topRight',
                                duration: 3,
                                style: { width: 280 },
                            })
                        }
                    } else if (data.type === 'new_message') {
                        fetchUnreadChats()
                        
                        // Only show push notifications if the message explicitly belongs to the logged-in user's assigned account.
                        // This prevents teamleads from getting spam popups from unassigned accounts or accounts assigned to other users.
                        const isMyAccount = user && data.operator_id === user.id;
                        
                        if (data.message && !data.message.outgoing && isMyAccount) {
                            // Add to bell notification store
                            const accountId = data.account_id
                            const chatId = data.chat_id
                            addTgMessage({
                                id: Date.now(),
                                message_id: data.message.id,
                                account_id: accountId,
                                chat_id: chatId,
                                topic_id: data.topic_id ?? data.message.topic_id,
                                sender: data.message.from_user?.first_name || 'TG',
                                text: data.message.text || '📎 Медиа',
                                date: data.message.date,
                            })
                            // Всплывающее уведомление (если включены)
                            if (notificationsEnabledRef.current) {
                                const senderName = data.message.from_user?.first_name || 'Сообщение'
                                const msgPreview = data.message.text || '📎 Медиа'
                                api.open({
                                    key: `tg_msg_${data.message.id}`,
                                    message: senderName,
                                    description: msgPreview,
                                    placement: 'topRight',
                                    duration: 3,
                                    style: { width: 260, cursor: 'pointer' },
                                    onClick: () => {
                                        if (chatId) {
                                            const params = new URLSearchParams()
                                            params.set('chat', String(chatId))
                                            if (accountId) params.set('account', String(accountId))
                                            if (data.message.id) params.set('messageId', String(data.message.id))
                                            if (data.topic_id ?? data.message.topic_id) params.set('topicId', String(data.topic_id ?? data.message.topic_id))
                                            navigate(`/chats?${params.toString()}`)
                                        }
                                        notification.destroy(`tg_msg_${data.message.id}`)
                                    },
                                })
                            }
                        }
                    } else if (data.type === 'chat_message' || data.type === 'chat_update') {
                        fetchUnreadChats()
                    }
                } catch (e) {
                    console.error('WS parse error:', e)
                }
            }

            ws.onclose = () => {
                if (!wsIsClosed) {
                    reconnectTimeout = setTimeout(() => {
                        reconnectDelay = Math.min(reconnectDelay * 2, 30000)
                        connectWs()
                    }, reconnectDelay)
                }
            }
            ws.onerror = () => { ws?.close() }
        }

        connectWs()

        return () => {
            clearInterval(interval)
            wsIsClosed = true
            if (reconnectTimeout) clearTimeout(reconnectTimeout)
            if (ws) ws.close()
        }
    }, [])

    const fetchUnreadChats = async () => {
        try {
            const res = await internalChatsApi.getMyChats()
            if (res.data) {
                const count = res.data.reduce((acc: number, chat: any) => acc + chat.unread_count, 0)
                setUnreadChatCount(count)
            }
        } catch (e) {
            console.error('Failed to fetch unread chats count', e)
        }
    }

    const isAdmin = user?.role === 'teamlead'
    const isDark = theme === 'dark'

    const menuItems = [
        {
            key: '/',
            icon: <DashboardIcon size={20} />,
            label: 'Дашборд',
        },
        {
            key: '/chats',
            icon: <ChatsIcon size={20} />,
            label: 'Чаты',
        },
        {
            key: '/contacts',
            icon: <ContactsIcon size={20} />,
            label: 'Контакты',
        },
        {
            key: '/calendar',
            icon: <CalendarIcon size={20} />,
            label: 'Календарь',
        },
        {
            key: '/tasks',
            icon: <TasksIcon size={20} />,
            label: 'Задачи',
        },
        {
            key: '/broadcasts',
            icon: <SendIcon size={20} />,
            label: 'TG Рассылки',
        },
        /*
        {
            key: '/email/campaigns',
            icon: <MailOutlined style={{ fontSize: 20 }} />,
            label: 'Email Рассылки',
        },
        */
    ]

    const adminItems = [
        { type: 'divider' as const },
        {
            key: '/admin/operators',
            icon: <OperatorsIcon size={20} />,
            label: 'Пользователи',
        },
        {
            key: '/admin/accounts',
            icon: <TelegramAccountIcon size={20} />,
            label: 'TG Аккаунты',
        },
        {
            key: '/admin/proxies',
            icon: <ProxyIcon size={20} />,
            label: 'Прокси',
        },
    ]

    const allMenuItems = [...menuItems, ...adminItems]

    const userMenuItems = [
        {
            key: 'settings',
            icon: <SettingsIcon size={16} />,
            label: 'Настройки',
        },
        {
            key: 'logout',
            icon: <LogoutOutlined />,
            label: 'Выйти',
            danger: true,
        },
    ]

    const handleUserMenu = ({ key }: { key: string }) => {
        if (key === 'logout') {
            logout()
            navigate('/login')
        } else if (key === 'settings') {
            navigate('/settings')
        }
    }

    return (
        <AntLayout style={{ minHeight: '100vh' }}>
            <Sider
                collapsible
                collapsed={collapsed}
                onCollapse={setCollapsed}
                trigger={null}
                theme={isDark ? 'dark' : 'light'}
                style={{
                    background: 'var(--bg-surface)',
                    borderRight: '1px solid var(--border)',
                    boxShadow: '2px 0 8px rgba(0,0,0,0.05)',
                }}
                width={260}
                collapsedWidth={80}
            >
                <div
                    style={{
                        height: 80,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: collapsed ? 'center' : 'flex-start',
                        padding: collapsed ? '0' : '0 24px',
                        borderBottom: '1px solid var(--border)',
                        gap: 14,
                        background: isDark
                            ? 'linear-gradient(180deg, rgba(0,136,204,0.08) 0%, transparent 100%)'
                            : 'linear-gradient(180deg, rgba(0,136,204,0.05) 0%, transparent 100%)',
                    }}
                >
                    <div
                        className="logo-container"
                        style={{
                            width: 44,
                            height: 44,
                            borderRadius: 12,
                            background: 'linear-gradient(135deg, #0088cc 0%, #00b4d8 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 4px 16px rgba(0, 136, 204, 0.4)',
                            flexShrink: 0,
                            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                            cursor: 'pointer',
                        }}
                        onClick={() => navigate('/')}
                    >
                        <SendIcon size={22} className="text-white" />
                    </div>
                    {!collapsed && (
                        <div style={{ animation: 'fadeIn 0.3s ease' }}>
                            <div style={{
                                fontSize: 18,
                                fontWeight: 800,
                                color: 'var(--text-primary)',
                                lineHeight: 1.2,
                                letterSpacing: '-0.02em',
                            }}>
                                TG <span style={{
                                    background: 'linear-gradient(135deg, #0088cc 0%, #00b4d8 100%)',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                }}>Manager</span>
                            </div>
                            <div style={{
                                fontSize: 11,
                                color: 'var(--text-muted)',
                                fontWeight: 600,
                                letterSpacing: '0.05em',
                                textTransform: 'uppercase',
                            }}>
                                Telegram CRM
                            </div>
                        </div>
                    )}
                </div>

                <Menu
                    theme={isDark ? 'dark' : 'light'}
                    mode="inline"
                    selectedKeys={[location.pathname]}
                    items={allMenuItems}
                    onClick={({ key }) => navigate(key)}
                    style={{
                        background: 'transparent',
                        borderRight: 0,
                        padding: '16px 0',
                    }}
                />

                <div
                    style={{
                        position: 'absolute',
                        bottom: 16,
                        left: 0,
                        right: 0,
                        padding: '0 16px',
                    }}
                >
                    <Button
                        type="text"
                        icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                        onClick={() => setCollapsed(!collapsed)}
                        style={{
                            width: '100%',
                            height: 44,
                            borderRadius: 10,
                            color: 'var(--text-secondary)',
                            background: 'var(--bg-subtle)',
                        }}
                    />
                </div>
            </Sider>

            <AntLayout>
                <Header
                    style={{
                        background: 'var(--bg-surface)',
                        borderBottom: '1px solid var(--border)',
                        padding: '0 28px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: 12,
                        height: 68,
                        boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                    }}
                >
                    <Tooltip title={isDark ? 'Светлая тема' : 'Тёмная тема'}>
                        <Button
                            type="text"
                            onClick={toggleTheme}
                            className="theme-btn"
                            style={{
                                width: 44,
                                height: 44,
                                borderRadius: 12,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: isDark ? '#fbbf24' : '#6366f1',
                                background: isDark ? 'rgba(251, 191, 36, 0.1)' : 'rgba(99, 102, 241, 0.1)',
                                border: `1px solid ${isDark ? 'rgba(251, 191, 36, 0.2)' : 'rgba(99, 102, 241, 0.2)'}`,
                                transition: 'all 0.3s ease',
                            }}
                        >
                            {isDark ? <SunOutlined style={{ fontSize: 20 }} /> : <MoonOutlined style={{ fontSize: 20 }} />}
                        </Button>
                    </Tooltip>

                    <Popover
                        content={<ChatNotificationList onClose={() => setChatPopoverOpen(false)} />}
                        trigger="click"
                        open={chatPopoverOpen}
                        onOpenChange={setChatPopoverOpen}
                        placement="bottomRight"
                        overlayInnerStyle={{ padding: 0 }}
                    >
                        <Tooltip title="Внутренние чаты">
                            <Badge count={unreadChatCount} size="small" offset={[-4, 4]}>
                                <Button
                                    type="text"
                                    icon={<span style={{ fontSize: 22 }}>💬</span>}
                                    className="header-icon-btn"
                                    style={{
                                        width: 44,
                                        height: 44,
                                        borderRadius: 12,
                                        color: chatPopoverOpen ? '#1890ff' : 'var(--text-secondary)',
                                        background: chatPopoverOpen ? 'rgba(24,144,255,0.1)' : 'var(--bg-subtle)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                />
                            </Badge>
                        </Tooltip>
                    </Popover>

                    <Tooltip title={notificationsEnabled ? 'Выключить пуши' : 'Включить пуши'}>
                        <Button
                            type="text"
                            onClick={() => {
                                const next = !notificationsEnabled
                                setNotificationsEnabled(next)
                                notificationsEnabledRef.current = next
                                localStorage.setItem('tg_notifications_enabled', String(next))
                            }}
                            style={{
                                width: 44,
                                height: 44,
                                borderRadius: 12,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: notificationsEnabled ? '#0088cc' : 'var(--text-muted)',
                                background: notificationsEnabled ? 'rgba(0,136,204,0.1)' : 'var(--bg-subtle)',
                                border: `1px solid ${notificationsEnabled ? 'rgba(0,136,204,0.2)' : 'var(--border)'}`,
                                transition: 'all 0.25s ease',
                            }}
                        >
                            {notificationsEnabled
                                ? <BellFilled style={{ fontSize: 20 }} />
                                : <BellOutlined style={{ fontSize: 20, opacity: 0.4 }} />
                            }
                        </Button>
                    </Tooltip>

                    <Popover
                        content={<NotificationList onClose={() => setNotificationOpen(false)} />}
                        trigger="click"
                        open={notificationOpen}
                        onOpenChange={(open) => {
                            setNotificationOpen(open)
                            if (!open) clearTgUnread()
                        }}
                        placement="bottomRight"
                        overlayInnerStyle={{ padding: 0 }}
                    >
                        <Badge count={(unreadCount || 0) + (tgUnreadCount || 0)} size="small" offset={[-4, 4]}>
                            <Button
                                type="text"
                                icon={<span style={{ fontSize: 20 }}>🔔</span>}
                                style={{
                                    width: 44,
                                    height: 44,
                                    borderRadius: 12,
                                    color: notificationOpen ? '#0088cc' : 'var(--text-secondary)',
                                    background: notificationOpen ? 'rgba(0,136,204,0.1)' : 'var(--bg-subtle)',
                                }}
                            />
                        </Badge>
                    </Popover>

                    <Dropdown
                        menu={{ items: userMenuItems, onClick: handleUserMenu }}
                        placement="bottomRight"
                    >
                        <div
                            className="user-dropdown"
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 14,
                                cursor: 'pointer',
                                padding: '10px 18px',
                                borderRadius: 14,
                                transition: 'all 0.25s ease',
                                background: 'var(--bg-subtle)',
                                border: '1px solid var(--border)',
                            }}
                        >
                            <Avatar
                                size={40}
                                src={user?.avatar_url}
                                style={{
                                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                                    boxShadow: '0 4px 14px rgba(99, 102, 241, 0.35)',
                                    fontWeight: 700,
                                    fontSize: 16,
                                }}
                            >
                                {!user?.avatar_url && (user?.full_name || user?.username || 'U')[0].toUpperCase()}
                            </Avatar>
                            <div style={{ lineHeight: 1.3 }}>
                                <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)' }}>
                                    {user?.full_name || user?.username}
                                </div>
                                <div style={{
                                    fontSize: 11,
                                    color: 'var(--text-muted)',
                                    fontWeight: 600,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.03em',
                                }}>
                                    {user?.role === 'teamlead' ? 'Тимлид' : 'Оператор'}
                                </div>
                            </div>
                        </div>
                    </Dropdown>
                </Header>

                <Content
                    style={{
                        background: 'var(--bg-base)',
                        minHeight: 'calc(100vh - 68px)',
                        overflow: 'auto',
                        transition: 'background-color 0.3s ease',
                    }}
                >
                    <div className="animate-fade-in-up" style={{ animationDuration: '0.4s' }}>
                        {children}
                    </div>
                </Content>
            </AntLayout>


            {contextHolder}
        </AntLayout>
    )
}
